import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Nav } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import config from '../../../config';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import TpmMaintenanceList from './TpmMaintenanceList';
import TpmTipsModal from './TpmTipsModal';
import { getStoredUser, getSessionHeaders } from './tpmRequest';

const DEFAULT_ROWS_PER_PAGE = 10;

function getChecklistRows(body) {
    if (Array.isArray(body)) return body;
    return body && Array.isArray(body.data) ? body.data : [];
}

function getTotalRows(body, rows) {
    if (body && body.meta && Number.isFinite(Number(body.meta.total_count))) return Number(body.meta.total_count);
    if (body && body.pagination && Number.isFinite(Number(body.pagination.total))) return Number(body.pagination.total);
    return rows.length;
}

function formatUpdatedDate(row) {
    const value = row && (row.updatedAt || row.updated_at || row.updatedDate || row.updated_date || row.createdAt || row.created_at);
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
}

function getInterval(row, tierKey) {
    let tier = row && row[tierKey];
    if (typeof tier === 'string') {
        try {
            tier = JSON.parse(tier);
        } catch (_error) {
            tier = null;
        }
    }
    if (tier && typeof tier === 'object') return tier.intervalPcs || '';
    const legacyValue = row && row[`${tierKey}IntervalPcs`];
    return legacyValue === null || legacyValue === undefined ? '' : legacyValue;
}

export default function ToolingTPM() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('checklist');
    const [showTips, setShowTips] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [activeKeyword, setActiveKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const requestSequence = useRef(0);

    const loadChecklists = useCallback((page, limit, searchKeyword) => {
        const user = getStoredUser();
        if (!user || !user.accountId) {
            setRows([]);
            setTotalRows(0);
            setError('Please sign in to view TPM checklists.');
            return Promise.resolve();
        }

        const offset = (page - 1) * limit;
        const requestId = ++requestSequence.current;
        const params = new URLSearchParams({
            company: String(user.accountId),
            keyword: searchKeyword || '',
            limit: String(limit),
            offset: String(offset)
        });

        setLoading(true);
        setError('');
        return fetch(`${config.apiUrl}/getTpmChecklistsByCompany?${params.toString()}`, {
            method: 'POST',
            headers: getSessionHeaders(user)
        })
            .then(async response => {
                const text = await response.text();
                let body = {};
                try {
                    body = text ? JSON.parse(text) : {};
                } catch (_error) {
                    body = {};
                }
                if (!response.ok) throw new Error(body.error || body.message || 'Failed to load TPM checklists.');
                return body;
            })
            .then(body => {
                if (requestId !== requestSequence.current) return;
                const records = getChecklistRows(body);
                setRows(records);
                setTotalRows(getTotalRows(body, records));
                setLoading(false);
            })
            .catch(requestError => {
                if (requestId !== requestSequence.current) return;
                setRows([]);
                setTotalRows(0);
                setError(requestError.message || 'Failed to load TPM checklists.');
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        loadChecklists(currentPage, rowsPerPage, activeKeyword);
    }, [activeKeyword, currentPage, loadChecklists, rowsPerPage]);

    const columns = [
        { name: 'Part No', selector: row => row.partNo || '', sortable: true },
        { name: 'Tooling No', selector: row => row.toolingNo || '', sortable: true },
        { name: 'Tier A (PCS)', selector: row => getInterval(row, 'tierA'), sortable: true },
        { name: 'Tier B (PCS)', selector: row => getInterval(row, 'tierB'), sortable: true },
        { name: 'Tier C (PCS)', selector: row => getInterval(row, 'tierC'), sortable: true },
        { name: 'Updated', selector: row => formatUpdatedDate(row), sortable: true },
        {
            name: '',
            cell: row => (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'nowrap', minWidth: '230px', paddingRight: '10px' }}>
                    <button
                        style={{ whiteSpace: 'nowrap', minWidth: '70px', padding: '6px 12px', fontSize: '14px', fontWeight: '500', flexShrink: 0 }}
                        type="button"
                        onClick={() => navigate(`/tooling/tpm/checklist/${row.id}`)}
                    >
                        Edit
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            width: '160px'
        }
    ];

    const isChecklistTab = activeTab === 'checklist';

    return (
        <div className="container">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 my-3">
                <h1 className="h3 mb-0">TPM</h1>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" type="button" onClick={() => setShowTips(true)}>Tier Tips</Button>
                    {isChecklistTab && (
                        <Link className="btn btn-primary" to="/tooling/tpm/checklist/new">New Checklist</Link>
                    )}
                </div>
            </div>

            <Nav
                variant="tabs"
                role="tablist"
                activeKey={activeTab}
                onSelect={key => key && setActiveTab(key)}
                className="mb-3"
            >
                <Nav.Item role="presentation">
                    <Nav.Link eventKey="checklist" role="tab" aria-selected={isChecklistTab}>
                        Master Checklist
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item role="presentation">
                    <Nav.Link eventKey="maintenance" role="tab" aria-selected={!isChecklistTab}>
                        Maintenance Records
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {isChecklistTab ? (
                <div>
                    {error && <div className="alert alert-danger" role="alert">{error}</div>}
                    <Form className="d-flex gap-2 flex-wrap mb-3" onSubmit={event => {
                        event.preventDefault();
                        setCurrentPage(1);
                        setActiveKeyword(keyword);
                    }}>
                        <Form.Control
                            aria-label="Search TPM checklists"
                            value={keyword}
                            placeholder="Search..."
                            onChange={event => setKeyword(event.target.value)}
                            style={{ maxWidth: '280px' }}
                        />
                        <Button type="submit">Search</Button>
                    </Form>
                    <DataTable
                        columns={columns}
                        data={rows}
                        progressPending={loading}
                        progressComponent={<DatacoLoadingIndicator loading={loading} size="compact" label="Loading TPM checklists" />}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={rowsPerPage}
                        paginationDefaultPage={currentPage}
                        onChangePage={page => setCurrentPage(page)}
                        onChangeRowsPerPage={(limit, page) => {
                            setRowsPerPage(limit);
                            setCurrentPage(page);
                        }}
                        highlightOnHover
                    />
                </div>
            ) : (
                <TpmMaintenanceList />
            )}

            <TpmTipsModal show={showTips} onHide={() => setShowTips(false)} />
        </div>
    );
}
